import { $menu } from '@/views/menu/menu';
// @ts-ignore
import template from "./menu.html";

import FilterButtons from "./FilterButtons";
import ContentButtons from "./ContentButtons";
import ToolButtons from "../../enum/TOOL_PRESETS";
import ProfileButtons from "./ProfileButtons";
import renderButtons from "./renderButtons";
import adjustFontSize from "../../tools/adjustFontSize";
import renderTools from "./renderTools";
import reset from "./reset";
import translateWidget from "./translateWidget";


import css from "./menu.css";
import enableContrast from "@/tools/enableContrast";
import { pluginConfig } from "@/globals/pluginConfig";
import {
  userSettings,
  setUserStateSettings,
  saveUserSettings,
} from "@/globals/userSettings";
import { changeLanguage } from "@/i18n/changeLanguage";
import toggleMenu from "./toggleMenu";
import { $widget } from "../widget/widget";
import enableProfile from "@/tools/enableProfile";
import disableContrast from "@/tools/disableContrast";
import disableProfile from "@/tools/disableProfile";
import selectButtons from "@/tools/selectButtons";
import { renderLanguageDropdown } from './renderLanguageDropdown';
import { getCurrentDevice } from "@/utils/device";
import trapFocus from "@/utils/trapFocus";

/**
 * Announces changes to screen readers
 * @param message - The message to announce
 */
function announceToScreenReader(message: string): void {
  const announcements = document.getElementById('cya11y-menu-announcements');
  if (announcements) {
    announcements.textContent = message;
    // Clear the message after a short delay
    setTimeout(() => {
      announcements.textContent = '';
    }, 1000);
  }
}

/**
 * Updates ARIA pressed state for buttons
 * @param button - The button element
 * @param isPressed - Whether the button is pressed
 */
function updateButtonAriaState(button: HTMLElement, isPressed: boolean): void {
  button.setAttribute('aria-pressed', isPressed.toString());
  if (isPressed) {
    button.classList.add("cya11y-selected");
  } else {
    button.classList.remove("cya11y-selected");
  }
}

/**
 * Handles keyboard navigation for menu items
 * @param event - Keyboard event
 * @param currentButton - Current button element
 */
function handleMenuKeyboardNavigation(event: KeyboardEvent, currentButton: HTMLElement): void {
  const buttons = Array.from(currentButton.parentElement?.querySelectorAll('.cya11y-btn') || []) as HTMLElement[];
  const currentIndex = buttons.indexOf(currentButton);
  
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % buttons.length;
      buttons[nextIndex]?.focus();
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      const prevIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
      buttons[prevIndex]?.focus();
      break;
    case 'Home':
      event.preventDefault();
      buttons[0]?.focus();
      break;
    case 'End':
      event.preventDefault();
      buttons[buttons.length - 1]?.focus();
      break;
  }
}

export default function renderMenu() {
  const device = getCurrentDevice();
  const $container: HTMLElement = document.createElement("div");
  $container.innerHTML = `<style id="cya11y-menu-css">${css}</style>` + template;
  const $menu = $container.querySelector(".cya11y-menu") as HTMLElement;
  
  // Position menu based on widget position
  const currentPosition = pluginConfig?.position[device];
  if (currentPosition) {
    // Set data-position attribute for CSS positioning
    $menu.setAttribute('data-position', currentPosition);
    
    if (currentPosition.includes("right")) {
      $menu.style.right = "0px";
      $menu.style.left = "auto";
    } else if (currentPosition.includes("left")) {
      $menu.style.left = "0px";
      $menu.style.right = "auto";
    }
  }

  // Add global escape key handler for the menu
  const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      toggleMenu();
      // Remove the event listener after closing
      document.removeEventListener('keydown', handleEscapeKey);
    }
  };

  // Add escape key listener when menu is opened
  document.addEventListener('keydown', handleEscapeKey);

  // Store the handler reference for cleanup
  ($menu as any)._escapeKeyHandler = handleEscapeKey;

  const contentElement = $menu.querySelector(".content");
  const toolsElement = $menu.querySelector(".tools");
  const contrastElement = $menu.querySelector(".contrast");
  const profilesElement = $menu.querySelector(".profiles");

  if (contentElement) {
    contentElement.innerHTML = renderButtons(ContentButtons);
  }
  if (toolsElement) {
    toolsElement.innerHTML = renderButtons(
      ToolButtons as any,
      "cya11y-tools"
    );
  }
  if (contrastElement) {
    contrastElement.innerHTML = renderButtons(
      FilterButtons,
      "cya11y-filter"
    );
  }
  if (profilesElement) {
    profilesElement.innerHTML = renderButtons(
      ProfileButtons,
      "cya11y-profile"
    );
  }
  
  const $profileCard = $menu.querySelector(".cya11y-profile-card") as HTMLElement;
  const $profileTitle = $menu.querySelector(".cya11y-profile-title") as HTMLElement;
  const $profileItems = $menu.querySelector(".cya11y-profile-items") as HTMLElement;
  
  // Hide profile items by default
  if ($profileItems) {
    $profileItems.style.display = 'none';
  }

  if ($profileTitle) {
    $profileTitle.addEventListener("click", () => {
      const $profileItems = $menu.querySelector(".cya11y-profile-items") as HTMLElement;
      const isExpanded = $profileItems.style.display !== 'none';
      
      // Toggle visibility of profile items
      $profileItems.style.display = isExpanded ? 'none' : 'grid';
      
      // Update ARIA state
      $profileTitle.setAttribute('aria-expanded', (!isExpanded).toString());
      $profileCard.querySelector(".cya11y-arrow")?.classList.toggle("cya11y-arrow-rotate");
      
      // Announce to screen reader
      announceToScreenReader(isExpanded ? 'Accessibility profiles collapsed' : 'Accessibility profiles expanded');
    });
  }
  
  $profileCard.remove();
  
  // *** States UI Rendering ***
  const states = userSettings?.states;

  const fontSize = Number(states?.fontSize) || 1;
  if (fontSize != 1) {
    const amountElement = $menu.querySelector(".cya11y-amount");
    if (amountElement) {
      amountElement.innerHTML = `${(fontSize * 100).toFixed(0)}%`;
    }
  }

  selectButtons($menu);

  renderLanguageDropdown($menu);

  // Handle accessibility statement link
  const $statementLink = $container.querySelector('.cya11y-accessibility-statement-link') as HTMLAnchorElement;
  if ($statementLink) {
    if (pluginConfig.modules.statement.enabled) {
      $statementLink.href = pluginConfig.modules.statement.url || '#';
      $statementLink.style.display = 'block';
    } else {
      $statementLink.style.display = 'none';
    }
  }

  // *** Utils ***
  $container
    .querySelectorAll(".cya11y-menu-close, .cya11y-overlay")
    .forEach((el) => el.addEventListener("click", toggleMenu));

  $container
    .querySelectorAll(".cya11y-menu-reset")
    .forEach((el) => el.addEventListener("click", () => {
      reset($menu);
      announceToScreenReader('All accessibility settings have been reset to default');
    }));

  // *** Controls ***
  $menu.querySelectorAll(".cya11y-plus, .cya11y-minus").forEach((el) => {
    const element = el as HTMLElement;
    element.addEventListener("click", () => {
      const difference = 0.1;

      let fontSize = userSettings?.states?.fontSize || 1;
      if (element.classList.contains("cya11y-minus")) {
        fontSize -= difference;
      } else {
        fontSize += difference;
      }

      fontSize = Math.max(fontSize, 0.1);
      fontSize = Math.min(fontSize, 4);
      fontSize = Number(fontSize.toFixed(4));

      const amountElement = $container.querySelector(".cya11y-amount") as HTMLElement;
      if (amountElement) {
        amountElement.textContent = `${(fontSize * 100).toFixed(0)}%`;
      }

      adjustFontSize(fontSize);
      userSettings.states.fontSize = fontSize;

      saveUserSettings();
      
      // Announce font size change
      announceToScreenReader(`Font size set to ${(fontSize * 100).toFixed(0)}%`);
    });
  });

  $menu.querySelectorAll(".cya11y-btn").forEach((el) => {
    const element = el as HTMLElement;
    
    // Add keyboard navigation
    element.addEventListener("keydown", (event) => {
      handleMenuKeyboardNavigation(event, element);
    });
    
    element.addEventListener("click", () => {
        const key = element.dataset.key;
        if (!key) return; // Early return if key is undefined
        
        const isSelected = !element.classList.contains("cya11y-selected");

      // --- Contrast ---
        if (element.classList.contains("cya11y-filter")) {
            $menu
            .querySelectorAll(".cya11y-filter")
            .forEach((el) => {
              const filterElement = el as HTMLElement;
              updateButtonAriaState(filterElement, false);
            });

            if (isSelected) {
                updateButtonAriaState(element, true);
            }
        
            if (userSettings.states.contrast === key) {
                userSettings.states.contrast = false;
                saveUserSettings();
                disableContrast();
                announceToScreenReader('Contrast filter disabled');
                return;
            }
            userSettings.states.contrast = isSelected ? key : false;
            enableContrast(userSettings.states.contrast);
            saveUserSettings();
            renderTools();
            updateButtonAriaState(element, isSelected);
            
            // Announce contrast change
            const contrastLabel = element.querySelector('.cya11y-translate')?.textContent || key;
            announceToScreenReader(`${contrastLabel} contrast enabled`);

            return;
        }

      // --- Profiles ---
      if (element.classList.contains("cya11y-profile")) {
        $menu
          .querySelectorAll(".cya11y-profile")
          .forEach((el) => {
            const profileElement = el as HTMLElement;
            updateButtonAriaState(profileElement, false);
          });

        if (isSelected) {
          updateButtonAriaState(element, true);
        }

        if(userSettings.states[key]){
            userSettings.states[key] = false;
            saveUserSettings();
            disableProfile(key, $menu);
            announceToScreenReader(`${key} profile disabled`);
            return;
        }
        Object.keys(userSettings.states).forEach((stateKey) => {
          if(stateKey.includes('profile')){
            userSettings.states[stateKey] = false;
            saveUserSettings();
            disableProfile(stateKey, $menu);
          }
        });
        saveUserSettings();
        userSettings.states[key]= true;
        enableProfile(key, $menu);
        saveUserSettings();
        renderTools();
        updateButtonAriaState(element, isSelected);
        
        // Announce profile change
        const profileLabel = element.querySelector('.cya11y-translate')?.textContent || key;
        announceToScreenReader(`${profileLabel} profile enabled`);
      }

      updateButtonAriaState(element, isSelected);
      userSettings.states[key] = isSelected;
      renderTools();

      saveUserSettings();
      
      // Announce general setting change
      const settingLabel = element.querySelector('.cya11y-translate')?.textContent || key;
      announceToScreenReader(`${settingLabel} ${isSelected ? 'enabled' : 'disabled'}`);
    });
  });

  if ($widget) {
    $widget.appendChild($container);
  }

  trapFocus($menu);

  return $menu;
}

